import type { Ref } from 'vue';
import type { CollectionFieldPath, Filter, LogicalFilterAND } from '#layers/data-model/public';
import type { FilterSchemaContext } from '#layers/filters/components/Filter/utils/filter-schema-context';
import { isEqual } from 'lodash-es';
import { createContext } from 'reka-ui';
import { computed, shallowRef, toRaw, watch } from 'vue';
import { isLogicalFilter } from '#layers/data-model/public';
import { injectFilterSchemaContext } from '#layers/filters/components/Filter/utils/filter-schema-context';
import { useFilterConfigResolver } from '#layers/filters/components/Filter/utils/use-filter-config-resolver';
import { useLogicalFilter } from '#layers/filters/composables/use-logical-filter';

export interface FilterRootContext {
	filter: Readonly<Ref<LogicalFilterAND>>;
	addFilter: (fieldPath: CollectionFieldPath) => void;
	addFilterWithValue: (fieldPath: CollectionFieldPath, value: unknown) => void;
	addCondition: (condition: Filter) => void;
	addGroup: () => void;
	removeAt: (index: number) => void;
	updateAt: (index: number, update: Filter) => void;
	clear: () => void;
	/** Changes when the root is replaced outside this context, so index-keyed children can reset local UI state. */
	replacementVersion: Readonly<Ref<number>>;
	/**
	 * True for the index a picker just inserted, until it reports back: the operator→value step
	 * machine in `FilterSimple`, the popover in `FilterAdvanced`.
	 */
	isPendingAdvance: (index: number) => boolean;
	clearPendingAdvance: () => void;
}

const [injectFilterRoot, provideFilterRootContext] = createContext<FilterRootContext>('FilterRoot');

export { injectFilterRoot };

export interface ProvideFilterRootOptions {
	/**
	 * Defaults to `injectFilterSchemaContext()`, which falls back to the workspace schema store.
	 * Pass it explicitly when the same component provides the schema context, since a component
	 * cannot inject what it provides itself.
	 */
	schemaContext?: FilterSchemaContext;
	/** Runs when a picker selection should reveal a hidden filter bar, including an existing group. */
	onAdded?: () => void;
}

/**
 * Owns every mutation of the root clause list, so a picker rendered outside `FilterBar` — the
 * listing toolbar, the audit log toolbar — drives the same state the bar renders from.
 */
export function provideFilterRoot(filter: Ref<LogicalFilterAND>, options: ProvideFilterRootOptions = {}): FilterRootContext {
	const { findPrimitiveFieldById, findSingleRelationFieldById } = options.schemaContext ?? injectFilterSchemaContext();
	const { supportedOperatorsFor, defaultOperatorFor } = useFilterConfigResolver();
	let expectedFilter: LogicalFilterAND | undefined;

	// A controlled model does not expose an emitted value until its parent renders it back. Capture
	// context-owned writes at the setter boundary so that round-trip is not mistaken for an external
	// replacement.
	const mutationFilter = computed<LogicalFilterAND>({
		get: () => filter.value,
		set: (value) => {
			expectedFilter = toRaw(value);
			filter.value = value;
		},
	});

	const { handleAdd, handleAddWithValue, handleAddCondition, handleRemove, handleUpdate, handleGroupAdd, handleClearFilter } = useLogicalFilter(mutationFilter, {
		addGroupToFront: true,
		findPrimitiveFieldById,
		findSingleRelationFieldById,
		supportedOperatorsFor,
		defaultOperatorFor,
	});

	const pendingAdvanceIndex = shallowRef<number | null>(null);
	const replacementVersion = shallowRef(0);

	watch(filter, (value) => {
		if (expectedFilter && isEqual(toRaw(value), expectedFilter)) {
			expectedFilter = undefined;
			return;
		}

		expectedFilter = undefined;
		pendingAdvanceIndex.value = null;
		replacementVersion.value += 1;
	}, { flush: 'sync' });

	function addFilter(fieldPath: CollectionFieldPath) {
		const index = handleAdd(fieldPath);
		if (index === undefined) return;

		pendingAdvanceIndex.value = index;
		options.onAdded?.();
	}

	function addFilterWithValue(fieldPath: CollectionFieldPath, value: unknown) {
		const index = handleAddWithValue(fieldPath, value);
		if (index === undefined) return;

		options.onAdded?.();
	}

	function addGroup() {
		const existingGroupIndex = (expectedFilter ?? filter.value)._and.findIndex((clause) => isLogicalFilter(clause));

		if (existingGroupIndex !== -1) {
			pendingAdvanceIndex.value = existingGroupIndex;
		}
		else {
			handleGroupAdd();
			pendingAdvanceIndex.value = 0;
		}

		options.onAdded?.();
	}

	function addCondition(condition: Filter) {
		handleAddCondition(condition);
		options.onAdded?.();
	}

	function removeAt(index: number) {
		const removesClause = index >= 0 && index < filter.value._and.length;
		handleRemove(index);

		if (!removesClause || pendingAdvanceIndex.value === null) return;
		if (pendingAdvanceIndex.value === index) pendingAdvanceIndex.value = null;
		else if (pendingAdvanceIndex.value > index) pendingAdvanceIndex.value -= 1;
	}

	function clear() {
		pendingAdvanceIndex.value = null;
		handleClearFilter();
	}

	function isPendingAdvance(index: number) {
		return index === pendingAdvanceIndex.value;
	}

	function clearPendingAdvance() {
		pendingAdvanceIndex.value = null;
	}

	const context: FilterRootContext = {
		filter,
		addFilter,
		addFilterWithValue,
		addCondition,
		addGroup,
		removeAt,
		updateAt: handleUpdate,
		clear,
		replacementVersion,
		isPendingAdvance,
		clearPendingAdvance,
	};

	provideFilterRootContext(context);

	return context;
}
